import React, { useRef, useEffect, useState } from 'react';
import './CustomInputRich.css';

// Componente input/textarea que permite mesclar texto e variáveis arrastáveis
export const InputRichVariavel = ({ value = '', onChange, placeholder = '', multiline = false }) => {
  const containerRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!value);

  // Atualiza o conteúdo visual quando value muda externamente
  useEffect(() => {
    if (containerRef.current && value !== getCurrentText()) {
      renderContent(value);
    }
  }, [value]);

  // Renderiza texto + chips de variáveis
  const renderContent = (textValue) => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';
    if (!textValue) {
      setIsEmpty(true);
      return;
    }
    setIsEmpty(false);
    // Divide por variáveis
    const parts = textValue.split(/(\{\{[^}]+\}\})/);
    parts.forEach(part => {
      if (/^\{\{[^}]+\}\}$/.test(part)) {
        const varName = part.slice(2, -2);
        const chip = document.createElement('span');
        chip.className = 'var-chip';
        chip.setAttribute('data-name', varName);
        chip.contentEditable = false;
        chip.innerHTML = `${varName}<button type="button">×</button>`;
        chip.querySelector('button').onclick = (e) => {
          e.stopPropagation();
          chip.remove();
          handleInput();
        };
        container.appendChild(chip);
        container.appendChild(document.createTextNode(' '));
      } else if (part) {
        // Suporte a quebras de linha no textarea
        if (multiline) {
          part.split('\n').forEach((line, idx, arr) => {
            if (line) container.appendChild(document.createTextNode(line));
            if (idx < arr.length - 1) container.appendChild(document.createElement('br'));
          });
        } else {
          container.appendChild(document.createTextNode(part));
        }
      }
    });
  };

  // Extrai texto + variáveis do DOM
  const getCurrentText = () => {
    const container = containerRef.current;
    if (!container) return '';
    let text = '';
    container.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.classList && node.classList.contains('var-chip')) {
        const varName = node.getAttribute('data-name');
        text += `{{${varName}}}`;
      } else if (node.tagName === 'BR') {
        text += '\n';
      }
    });
    return text;
  };

  // Atualiza valor ao digitar
  const handleInput = () => {
    const newText = getCurrentText();
    setIsEmpty(!newText);
    onChange(newText);
  };

  // Permite arrastar e soltar variáveis - sempre no final
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const data = e.dataTransfer.getData('application/variavel');
    if (!data) return;
    try {
      const { nome } = JSON.parse(data);
      const container = containerRef.current;
      container.focus();
      
      // Sempre insere no final do conteúdo
      const sel = window.getSelection();
      const range = document.createRange();
      
      // Posiciona no final do container
      range.selectNodeContents(container);
      range.collapse(false); // false = no final
      
      // Cria o chip da variável
      const chip = document.createElement('span');
      chip.className = 'var-chip';
      chip.setAttribute('data-name', nome);
      chip.contentEditable = false;
      chip.innerHTML = `${nome}<button type="button">×</button>`;
      chip.querySelector('button').onclick = (ev) => {
        ev.stopPropagation();
        chip.remove();
        handleInput();
      };
      
      // Insere chip no final
      range.insertNode(chip);
      range.setStartAfter(chip);
      
      // Adiciona espaço após o chip
      range.insertNode(document.createTextNode(' '));
      range.collapse(false);
      
      // Posiciona cursor após o espaço
      sel.removeAllRanges();
      sel.addRange(range);
      
      handleInput();
    } catch (error) {
      // Se não for variável, ignora
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Permite Enter para quebra de linha no textarea
  const handleKeyDown = (e) => {
    if (multiline && e.key === 'Enter') {
      e.preventDefault();
      const sel = window.getSelection();
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const br = document.createElement('br');
        range.insertNode(br);
        range.setStartAfter(br);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        handleInput();
      }
    }
    // Backspace/Delete para chips
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const sel = window.getSelection();
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const node = e.key === 'Backspace' 
          ? range.startContainer.previousSibling 
          : range.startContainer.nextSibling;
        if (node && node.classList && node.classList.contains('var-chip')) {
          e.preventDefault();
          node.remove();
          handleInput();
        }
      }
    }
  };

  // Foco e seleção normal - com prioridade sobre drag do node
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    container.focus();
    
    // Força o foco e posiciona cursor onde clicou
    setTimeout(() => {
      container.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      
      // Tenta posicionar cursor onde foi clicado
      if (document.caretRangeFromPoint) {
        const caretRange = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (caretRange && container.contains(caretRange.startContainer)) {
          sel.removeAllRanges();
          sel.addRange(caretRange);
          return;
        }
      }
      
      // Fallback: posiciona no final
      range.selectNodeContents(container);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }, 0);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Força foco imediato
    containerRef.current.focus();
  };

  const handleMouseUp = (e) => {
    e.stopPropagation();
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Seleciona palavra no duplo clique
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      sel.modify('extend', 'backward', 'word');
      sel.modify('extend', 'forward', 'word');
    }
  };

  // Renderiza
  return (
    <div
      ref={containerRef}
      className={`custom-input-rich${multiline ? ' custom-textarea' : ''} ${isEmpty ? 'empty' : ''}`}
      contentEditable={true}
      suppressContentEditableWarning={true}
      onInput={handleInput}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onDoubleClick={handleDoubleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onKeyDown={handleKeyDown}
      data-placeholder={placeholder}
      spellCheck={false}
      tabIndex={0}
      role="textbox"
      aria-multiline={multiline ? 'true' : 'false'}
      style={{
        outline: 'none',
        userSelect: 'text',
        WebkitUserSelect: 'text',
        MozUserSelect: 'text',
        msUserSelect: 'text',
        minHeight: multiline ? 80 : 38,
        resize: multiline ? 'vertical' : 'none',
        overflowY: multiline ? 'auto' : 'hidden',
        overflowX: 'hidden',
        cursor: 'text',
        position: 'relative',
        zIndex: 10,
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
      }}
    />
  );
};
